import { observer } from 'mobx-react-lite'
import {
  ModalDialog,
  ModalDialogContent,
  ModalDialogDescription,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogTitle,
} from '@/components/ModalDialog'
import { DependentProfileEditor } from '@/features/tax-profile/individual-profiles/components/DependentProfileEditor'
import type { DependentProfileEditorViewModel } from '@/features/tax-profile/individual-profiles/DependentProfileEditorViewModel'
import { Button } from '@/components/Button'
import { ButtonGroup } from '@/components/ButtonGroup'
import { useId } from 'react'

export interface DependentProfileEditorDialogContainerProps {
  vm: DependentProfileEditorViewModel
  isDisabled?: boolean
}

/**
 * Dialog for editing a dependent profile.
 */
export const DependentProfileEditorDialogContainer = observer(
  function DependentProfileEditorDialogContainer({
    vm,
    isDisabled,
  }: DependentProfileEditorDialogContainerProps) {
    const formId = useId()
    const isFormDisabled = vm.save.pending || vm.isReadOnly || isDisabled
    const testId = 'dependent-profile-editor-dialog'
    const saveButtonTestId = `${testId}-save-button`
    const cancelButtonTestId = `${testId}-cancel-button`
    const removeButtonTestId = `${testId}-remove-button`
    return (
      <ModalDialog
        isOpen={vm.modal.showing}
        onClose={vm.closeDialog}
        isDismissable={!vm.save.pending}
        sheetSizing="stretch"
        isUsingDialogScrolling
        size="lg"
        testId={testId}
      >
        <ModalDialogHeader>
          {vm.isReadOnly ? (
            <>
              <ModalDialogTitle>Dependent details</ModalDialogTitle>
              <ModalDialogDescription>
                View your dependent's details below.
              </ModalDialogDescription>
            </>
          ) : vm.isNew ? (
            <>
              <ModalDialogTitle>Add dependent's details</ModalDialogTitle>
              <ModalDialogDescription>
                Please provide the details of your dependent below.
              </ModalDialogDescription>
            </>
          ) : (
            <>
              <ModalDialogTitle>Edit dependent's details</ModalDialogTitle>
              <ModalDialogDescription>
                View your dependent's details below, and make changes if needed.
              </ModalDialogDescription>
            </>
          )}
        </ModalDialogHeader>

        <ModalDialogContent>
          <DependentProfileEditor
            formId={formId}
            isDisabled={isFormDisabled}
            vm={vm}
          />
          {!vm.isNew && !vm.isReadOnly && (
            <div className="pt-xl">
              <ButtonGroup stretch>
                <Button
                  variant="text-destructive"
                  label={`Remove ${vm.personalInfoVm.firstName} as a dependent`}
                  onPress={vm.remove}
                  isDisabled={isFormDisabled}
                  testId={removeButtonTestId}
                />
              </ButtonGroup>
            </div>
          )}
        </ModalDialogContent>
        <ModalDialogFooter sticky bordered compact>
          <ButtonGroup stretch>
            <Button
              isDisabled={vm.save.pending}
              variant="outline"
              label={vm.isReadOnly ? 'Close' : 'Cancel'}
              onPress={vm.closeDialog}
              testId={cancelButtonTestId}
            />
            {!vm.isReadOnly && (
              <Button
                isPending={vm.save.pending}
                isDisabled={isDisabled}
                variant="default"
                label={vm.isNew ? 'Save new dependent' : 'Save changes'}
                type="submit"
                form={formId}
                testId={saveButtonTestId}
              />
            )}
          </ButtonGroup>
        </ModalDialogFooter>
      </ModalDialog>
    )
  },
)
